import { type FieldValues, useController } from 'react-hook-form';

import { Combobox, type ComboboxProps } from '~/components/Combobox';

import { FormField, type FormFieldProps } from '../../FormField';

type FormFieldComboboxProps<
  TValue extends string,
  TFormFields extends FieldValues,
  TMultiselect extends boolean | undefined,
> = Omit<FormFieldProps<TFormFields>, 'children'> &
  Omit<ComboboxProps<TValue, TMultiselect>, 'name' | 'className'>;

// TODO: `state` prop support in standalone `Combobox` component
export function FormFieldCombobox<
  TValue extends string,
  TFormFields extends FieldValues,
  TMultiselect extends boolean | undefined,
>({
  name,
  label,
  className,
  disabled,
  readOnly,
  ...comboboxProps
}: FormFieldComboboxProps<TValue, TFormFields, TMultiselect>) {
  const {
    field,
    fieldState: { invalid },
  } = useController({ name, disabled });

  return (
    <FormField
      name={name}
      label={label}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
    >
      <Combobox
        {...field}
        state={invalid ? 'error' : undefined}
        {...comboboxProps}
        onChange={(newVal) => {
          field.onChange(newVal);

          // Trigger blur to ensure validation is run correctly
          field.onBlur();
        }}
        disabled={disabled || readOnly}
      />
    </FormField>
  );
}
FormFieldCombobox.displayName = 'FormFieldCombobox';
